import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { useRef, useState, useEffect, useMemo } from "react";
import { isItemDisabled } from "../../ProductService/isItemDisabled.ts";
import { Switch } from "../../UI/Switch.tsx";
import { buildListCountLabel } from "../buildListCountLabel.ts";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { SalesStatisticsButton } from "../SalesStatistics/SalesStatisticsButton.tsx";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { CreateNormasTeachingProductScreenPath } from "./CreateNormasTeachingProductScreen.tsx";
import { AdminNormasTeachingProductTable } from "./AdminNormasTeachingProductTable.tsx";
import { ErrorState } from "../../UI/ErrorState.tsx";

export const AdminAllNormasTeachingProductScreenPath =
  "/admin/normasteaching/all-products";
export function AdminAllNormasTeachingProductScreen() {
  const [searchInputValue, setSearchInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const refetchTimeoutRef = useRef<NodeJS.Timeout>();

  const { products, isLoading, error } = useAllNormasTeachingProducts({
    search,
  });
  const navigate = useNavigate();

  // Filtered here rather than server-side: `/products` is shared with the
  // coupon, promo and order screens, which must keep seeing every product.
  const visibleProducts = useMemo(
    () =>
      showDisabled ? products : products.filter((p) => !isItemDisabled(p)),
    [products, showDisabled],
  );

  const disabledCount = useMemo(
    () => products.filter(isItemDisabled).length,
    [products],
  );

  useEffect(() => {
    return () => clearTimeout(refetchTimeoutRef.current);
  }, []);

  if (isLoading) return <FullPageLoading />;
  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className={"flex-1 p-4 flex flex-col gap-4"}>
          <div
            className={
              "flex items-center justify-between gap-2 md:flex-row flex-col"
            }
          >
            <h1 className={"font-bold text-3xl flex-1"}>
              Prodotti Norma's Teaching
            </h1>
            <div className={"flex items-center gap-2"}>
              <SalesStatisticsButton />
              <PrimaryButton
                iconRight={<Plus className={"w-5"} />}
                label={"Nuovo prodotto"}
                onClick={() => navigate(CreateNormasTeachingProductScreenPath)}
              />
            </div>
          </div>
          <TextInput
            placeholder="Cerca per nome, classe, tag AC, analytics tag, ID, prezzo..."
            value={searchInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInputValue(value);
              clearTimeout(refetchTimeoutRef.current);
              refetchTimeoutRef.current = setTimeout(() => {
                setSearch(value);
              }, 500);
            }}
          />
          {error ? (
            <ErrorState />
          ) : (
            <div
              className={"flex items-center justify-between gap-4 flex-wrap"}
            >
              <p className={"text-sm text-gray-500"}>
                {buildListCountLabel({
                  visible: visibleProducts.length,
                  disabled: disabledCount,
                  showDisabled,
                  noun: ["prodotto", "prodotti"],
                })}
              </p>
              <Switch
                label={"Mostra disattivati"}
                checked={showDisabled}
                onChange={setShowDisabled}
              />
            </div>
          )}
          <div className={"overflow-x-auto"}>
            <AdminNormasTeachingProductTable products={visibleProducts} />
          </div>
        </div>
      </AdminMainContainer>

      <Footer />
    </Layout>
  );
}
